import { FC, useEffect, useState, useMemo } from 'react';
import { FinanceViewProps } from './finance.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import {
  useFinanceTabMenuItems,
  useFinanceAvailableRouteIds,
} from './finance.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { FinanceProvider } from './contexts/finance.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasAnySubmoduleReadPermission } from 'src/utils/rbacHelpers';

const FinanceView: FC<FinanceViewProps> = () => {
  const { translateText } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { financeSubRouteId } = useParams<{ financeSubRouteId: string }>();
  const navigate = useNavigate();
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();
  const availableRouteIds = useFinanceAvailableRouteIds();
  const tabs = useFinanceTabMenuItems(translateText);

  const hasAccess = useMemo(
    () => hasAnySubmoduleReadPermission(rbac, 'finance'),
    [rbac]
  );

  useEffect(() => {
    if (availableRouteIds.length === 0) return;

    const routeId =
      financeSubRouteId && financeSubRouteId !== 'undefined'
        ? financeSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.finance.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const tabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0);

    if (!financeSubRouteId || financeSubRouteId === 'undefined') {
      navigate(pages.finance.path + '/' + routeId, { replace: true });
    }
  }, [financeSubRouteId, navigate, availableRouteIds]);

  if (!hasAccess) {
    return <Navigate to={safeLanding} replace />;
  }

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      navigate(pages.finance.path + '/' + availableRouteIds[tabIndex], {
        replace: true,
      });
    }
  };

  return (
    <FinanceProvider>
      <NavigationDrawer
        selectedPage={pages.finance.path}
        window={window ?? null}
      >
        <Helmet>
          <title>{CONSTANTS.title.finance}</title>
        </Helmet>
        <Grid container id="financeViewContainer" sx={{ height: '100%' }} data-testid="finance-content-container">
          <Grid
            item
            xs={12}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              pr: { xs: 0, sm: 4 },
              gap: { xs: 1, sm: 0 },
              mb: { xs: 1, sm: 0 },
            }}
            data-testid="finance-view-header-container"
          >
            <MainTitle title={translateText('finance.title')} subTitle={selectedTab ? tabs[selectedTab].label : ''} />
          </Grid>
          <Grid item xs={12} sx={{ height: 'calc(100% - 64px)', px: 0 }}>
            <TabGroup
              key={selectedTab}
              tabs={tabs}
              initiallySelected={selectedTab}
              onTabChange={(value) => locationChangeHandler(value)}
              dataTestIdPrefix="finance"
              limitHeight={true}
            />
          </Grid>
        </Grid>
      </NavigationDrawer>
    </FinanceProvider>
  );
};

export default FinanceView;
